// eslint-disable-next-line no-restricted-imports
import React, { useEffect, useState } from 'react';
import {
  Button,
  ButtonProps,
  Dialog,
  Divider,
  Flex,
  Text,
  Textarea,
  useToasts,
} from '@applyboard/crystal-ui';
import { CopyOutlineIcon, ShareOutlineIcon } from '@applyboard/ui-icons';
import { copyToClipboard, useEventTracker, useTranslation } from '@applyboard/ui-utils';
import QrCodeGroup from '../common/components/QrCodeGroup';

interface btnOptions {
  name?: string;
  intent?: ButtonProps['intent'];
  disabled?: boolean;
  emphasis?: ButtonProps['emphasis'];
}

interface ExternalDialogControl {
  open: boolean;
  setOpen: (open: boolean) => void;
}

interface options {
  qrCodeUrl: string;
  title: string;
  description: string;
  eventPrefix: string;
  additionalTrackingData?: Record<string, unknown>;
  btnOptions?: btnOptions;
  externalDialogControl?: ExternalDialogControl;
}

const QrCodeDialog = (opts: options) => {
  const {
    qrCodeUrl,
    title,
    description,
    eventPrefix,
    additionalTrackingData = {},
    btnOptions,
    externalDialogControl,
  } = opts;
  const btnName = btnOptions?.name || 'Share Link';
  const btnIntent = btnOptions?.intent;
  const btnDisabled = btnOptions?.disabled === true;
  const btnEmphasis = btnOptions?.emphasis;
  const [internalOpen, setInternalOpen] = useState(externalDialogControl?.open || false);
  const { trackEvent } = useEventTracker();
  const { t } = useTranslation();
  const toasts = useToasts();
  const isExternallyControlled =
    externalDialogControl?.open !== undefined && externalDialogControl?.setOpen;

  const setDialogState = (state: boolean) => {
    if (isExternallyControlled) {
      externalDialogControl?.setOpen?.(state);
    }
    setInternalOpen(state);
  };

  useEffect(() => {
    if (isExternallyControlled) {
      setInternalOpen(externalDialogControl.open);
    }
  }, [isExternallyControlled, externalDialogControl?.open]);

  return (
    <Dialog onOpenChange={state => setDialogState(state)} size="md" open={internalOpen}>
      {!isExternallyControlled && (
        <Dialog.Trigger>
          <Button
            emphasis={btnEmphasis}
            intent={btnIntent}
            size="md"
            width="fill"
            leadIcon={ShareOutlineIcon}
            disabled={btnDisabled}
            onClick={() => {
              setInternalOpen(true);
              trackEvent({
                event: `${eventPrefix} - Button Clicked`,
                data: additionalTrackingData,
              });
            }}
          >
            {btnName}
          </Button>
        </Dialog.Trigger>
      )}
      <Dialog.Heading>{title}</Dialog.Heading>
      <Dialog.Content>
        <Text as="p">{description}</Text>

        <Flex align="start" direction="row" wrap={true} gap={1} pt={4} justify="center">
          <Flex align="center" direction="column" gap={0} justify="center" hideAbove="md">
            <QrCodeGroup
              qrCodeUrl={qrCodeUrl}
              label={t('hub.qrCode.title')} // good enough and generic enough for now, no need to customize yet
              eventTrackingData={{
                event: `${eventPrefix} - Download QR code button clicked`,
                data: additionalTrackingData,
              }}
            />
          </Flex>
          <Divider hideAbove="md" />

          <Flex align="start" direction="column" gap={4} justify="start">
            <Textarea
              intent="secondary"
              label="Link URL"
              onChange={() => {}}
              size="md"
              disabled={false}
              value={qrCodeUrl}
            />
            <Button
              emphasis="highlighted"
              intent="primary"
              size="md"
              leadIcon={CopyOutlineIcon}
              onClick={async () => {
                trackEvent({
                  event: `${eventPrefix} - Share Link Copied to Clipboard`,
                  data: additionalTrackingData,
                });
                await copyToClipboard(qrCodeUrl);
                toasts.positive('Shareable Link Copied!');
              }}
            >
              Copy link URL
            </Button>
          </Flex>

          <Divider hideBelow="md" direction="vertical" />

          <Flex align="center" direction="column" gap={0} justify="center" hideBelow="md">
            <QrCodeGroup
              qrCodeUrl={qrCodeUrl}
              label={t('hub.qrCode.title')}
              eventTrackingData={{
                event: `${eventPrefix} - Download QR code button clicked`,
                data: additionalTrackingData,
              }}
            />
          </Flex>
        </Flex>

        <Divider />
      </Dialog.Content>
      <Dialog.Actions>
        <Dialog.CloseButton size="md" intent="primary" width="fill" emphasis="contained">
          Done
        </Dialog.CloseButton>
      </Dialog.Actions>
    </Dialog>
  );
};

export default QrCodeDialog;
